import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Paper, Box, Typography } from '@mui/material';
import EmailVerification from './components/EmailVerification';
import OTPVerification from './components/OTPVerification';
import TokenValidation from './components/TokenValidation';
import PasswordReset from './components/PasswordReset';
import SuccessMessage from './components/SuccessMessage';
import StepIndicator from './components/StepIndicator';
import OTPSuccessMessage from './components/OTPSuccessMessage';

const STEPS = {
  EMAIL: 'email',
  OTP: 'otp',
  TOKEN: 'token',
  PASSWORD: 'password',
  SUCCESS: 'success',
  OTP_SUCCESS: 'otp_success'
};

// Thème personnalisé Material-UI
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#667eea',
      light: '#8fa4f3',
      dark: '#4c63d2',
    },
    secondary: {
      main: '#764ba2',
      light: '#9575cd',
      dark: '#512da8',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    success: {
      main: '#4caf50',
    },
    error: {
      main: '#f44336',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '12px 24px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },
});

function App() {
  const [currentStep, setCurrentStep] = useState(STEPS.EMAIL);
  const [resetData, setResetData] = useState({
    email: '',
    sessionId: '',
    token: '',
    userInfo: null
  });

  // Vérifier s'il y a un token dans l'URL au chargement
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    //console.log("🚀 ~ App ~ useEffect ~ token:", token)
    if (token) {
      setResetData(prev => ({ ...prev, token }));
      setCurrentStep(STEPS.TOKEN);
    }
  }, []);

  const updateResetData = (data) => {
    setResetData(prev => ({ ...prev, ...data }));
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  const getStepNumber = () => {
    switch (currentStep) {
      case STEPS.EMAIL: return 1;
      case STEPS.OTP: return 2;
      case STEPS.TOKEN: return 3;
      case STEPS.PASSWORD: return 4;
      case STEPS.SUCCESS: return 4;
      default: return 1;
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case STEPS.EMAIL:
        return (
          <EmailVerification
            onSuccess={(data) => {
              updateResetData(data);
              goToStep(STEPS.OTP);
            }}
            resetData={resetData}
          />
        );
      
      case STEPS.OTP:
        return (
          <OTPVerification
            onSuccess={() => goToStep(STEPS.OTP_SUCCESS)}
            onBack={() => goToStep(STEPS.EMAIL)}
            resetData={resetData}
            updateResetData={updateResetData}
          />
        );
      
      case STEPS.TOKEN:
        return (
          <TokenValidation
            onSuccess={(userInfo) => {
              updateResetData({ userInfo });
              goToStep(STEPS.PASSWORD);
            }}
            onError={() => goToStep(STEPS.EMAIL)}
            resetData={resetData}
          />
        );
      
      case STEPS.PASSWORD:
        return (
          <PasswordReset
            onSuccess={() => goToStep(STEPS.SUCCESS)}
            onBack={() => goToStep(STEPS.EMAIL)}
            resetData={resetData}
          />
        );
      
      case STEPS.SUCCESS:
        return <SuccessMessage />;

      case STEPS.OTP_SUCCESS:
        return <OTPSuccessMessage />;
      
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '70vh',
          background: '#FFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: '#FFF',
            }}
          >
            {/* En-tête */}
            <Box
              sx={{
                // background: 'linear-gradient(135deg, #2E7D32 0%, #2E7D32 100%)',
                background: '#339669',
                color: 'white',
                textAlign: 'center',
                py: 4,
                px: 3,
              }}
            >
              <Typography variant="h4" component="h1" gutterBottom>
                🔐 Réinitialisation
              </Typography>
              <Typography variant="h6" component="h2">
                Mot de passe
              </Typography>
              
              {currentStep !== STEPS.SUCCESS && (
                <Box sx={{ mt: 3 }}>
                  <StepIndicator 
                    currentStep={getStepNumber()} 
                    totalSteps={4} 
                  />
                </Box>
              )}
            </Box>
            
            {/* Corps */}
            <Box sx={{ p: 4 }}>
              {renderCurrentStep()}
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;